import { errorMessageFrom } from "@moeru/std";
import { stepCountAtLeast } from "@xsai/shared-chat";
import { streamText } from "@xsai/stream-text";
//#region src/runtime/agent-hooks.ts
function createChatHooks() {
	const onBeforeMessageComposedHooks = [];
	const onAfterMessageComposedHooks = [];
	const onBeforeSendHooks = [];
	const onAfterSendHooks = [];
	const onTokenLiteralHooks = [];
	const onTokenSpecialHooks = [];
	const onStreamEndHooks = [];
	const onAssistantResponseEndHooks = [];
	const onAssistantMessageHooks = [];
	const onChatTurnCompleteHooks = [];
	function onBeforeMessageComposed(cb) {
		onBeforeMessageComposedHooks.push(cb);
		return () => {
			const index = onBeforeMessageComposedHooks.indexOf(cb);
			if (index >= 0) onBeforeMessageComposedHooks.splice(index, 1);
		};
	}
	function onAfterMessageComposed(cb) {
		onAfterMessageComposedHooks.push(cb);
		return () => {
			const index = onAfterMessageComposedHooks.indexOf(cb);
			if (index >= 0) onAfterMessageComposedHooks.splice(index, 1);
		};
	}
	function onBeforeSend(cb) {
		onBeforeSendHooks.push(cb);
		return () => {
			const index = onBeforeSendHooks.indexOf(cb);
			if (index >= 0) onBeforeSendHooks.splice(index, 1);
		};
	}
	function onAfterSend(cb) {
		onAfterSendHooks.push(cb);
		return () => {
			const index = onAfterSendHooks.indexOf(cb);
			if (index >= 0) onAfterSendHooks.splice(index, 1);
		};
	}
	function onTokenLiteral(cb) {
		onTokenLiteralHooks.push(cb);
		return () => {
			const index = onTokenLiteralHooks.indexOf(cb);
			if (index >= 0) onTokenLiteralHooks.splice(index, 1);
		};
	}
	function onTokenSpecial(cb) {
		onTokenSpecialHooks.push(cb);
		return () => {
			const index = onTokenSpecialHooks.indexOf(cb);
			if (index >= 0) onTokenSpecialHooks.splice(index, 1);
		};
	}
	function onStreamEnd(cb) {
		onStreamEndHooks.push(cb);
		return () => {
			const index = onStreamEndHooks.indexOf(cb);
			if (index >= 0) onStreamEndHooks.splice(index, 1);
		};
	}
	function onAssistantResponseEnd(cb) {
		onAssistantResponseEndHooks.push(cb);
		return () => {
			const index = onAssistantResponseEndHooks.indexOf(cb);
			if (index >= 0) onAssistantResponseEndHooks.splice(index, 1);
		};
	}
	function onAssistantMessage(cb) {
		onAssistantMessageHooks.push(cb);
		return () => {
			const index = onAssistantMessageHooks.indexOf(cb);
			if (index >= 0) onAssistantMessageHooks.splice(index, 1);
		};
	}
	function onChatTurnComplete(cb) {
		onChatTurnCompleteHooks.push(cb);
		return () => {
			const index = onChatTurnCompleteHooks.indexOf(cb);
			if (index >= 0) onChatTurnCompleteHooks.splice(index, 1);
		};
	}
	function clearHooks() {
		onBeforeMessageComposedHooks.length = 0;
		onAfterMessageComposedHooks.length = 0;
		onBeforeSendHooks.length = 0;
		onAfterSendHooks.length = 0;
		onTokenLiteralHooks.length = 0;
		onTokenSpecialHooks.length = 0;
		onStreamEndHooks.length = 0;
		onAssistantResponseEndHooks.length = 0;
		onAssistantMessageHooks.length = 0;
		onChatTurnCompleteHooks.length = 0;
	}
	async function emitBeforeMessageComposedHooks(message, context) {
		for (const hook of onBeforeMessageComposedHooks) await hook(message, context);
	}
	async function emitAfterMessageComposedHooks(message, context) {
		for (const hook of onAfterMessageComposedHooks) await hook(message, context);
	}
	async function emitBeforeSendHooks(message, context) {
		for (const hook of onBeforeSendHooks) await hook(message, context);
	}
	async function emitAfterSendHooks(message, context) {
		for (const hook of onAfterSendHooks) await hook(message, context);
	}
	async function emitTokenLiteralHooks(literal, context) {
		for (const hook of onTokenLiteralHooks) await hook(literal, context);
	}
	async function emitTokenSpecialHooks(special, context) {
		for (const hook of onTokenSpecialHooks) await hook(special, context);
	}
	async function emitStreamEndHooks(context) {
		for (const hook of onStreamEndHooks) await hook(context);
	}
	async function emitAssistantResponseEndHooks(message, context) {
		for (const hook of onAssistantResponseEndHooks) await hook(message, context);
	}
	async function emitAssistantMessageHooks(message, messageText, context) {
		for (const hook of onAssistantMessageHooks) await hook(message, messageText, context);
	}
	async function emitChatTurnCompleteHooks(chat, context) {
		for (const hook of onChatTurnCompleteHooks) await hook(chat, context);
	}
	return {
		onBeforeMessageComposed,
		onAfterMessageComposed,
		onBeforeSend,
		onAfterSend,
		onTokenLiteral,
		onTokenSpecial,
		onStreamEnd,
		onAssistantResponseEnd,
		onAssistantMessage,
		onChatTurnComplete,
		emitBeforeMessageComposedHooks,
		emitAfterMessageComposedHooks,
		emitBeforeSendHooks,
		emitAfterSendHooks,
		emitTokenLiteralHooks,
		emitTokenSpecialHooks,
		emitStreamEndHooks,
		emitAssistantResponseEndHooks,
		emitAssistantMessageHooks,
		emitChatTurnCompleteHooks,
		clearHooks
	};
}
//#endregion
//#region src/runtime/context-registry.ts
const CONTEXT_UPDATE_REPLACE_SELF = "replace-self";
const CONTEXT_UPDATE_APPEND_SELF = "append-self";
function formatMetadataSource(source) {
	if (!source?.plugin) return void 0;
	const pluginId = source.plugin.id;
	const instanceId = source.id;
	return instanceId ? `${pluginId}:${instanceId}` : pluginId;
}
function defaultGetSourceKey(event, fallback = "unknown") {
	return formatMetadataSource(event.metadata?.source) ?? event.source ?? fallback;
}
function createContextRegistry(options = {}) {
	const historyLimit = options.historyLimit ?? 400;
	const getSourceKey = options.getSourceKey ?? defaultGetSourceKey;
	let currentActiveContexts = {};
	let currentContextHistory = [];
	function ingest(envelope) {
		const sourceKey = getSourceKey(envelope);
		if (!currentActiveContexts[sourceKey]) currentActiveContexts[sourceKey] = [];
		const safeEnvelopeToStore = structuredClone(envelope);
		if (envelope.strategy === CONTEXT_UPDATE_REPLACE_SELF) currentActiveContexts[sourceKey] = [safeEnvelopeToStore];
		else if (envelope.strategy === CONTEXT_UPDATE_APPEND_SELF) currentActiveContexts[sourceKey].push(safeEnvelopeToStore);
		currentContextHistory = [...currentContextHistory, {
			...safeEnvelopeToStore,
			sourceKey
		}].slice(-historyLimit);
	}
	function reset() {
		currentActiveContexts = {};
		currentContextHistory = [];
	}
	function snapshot() {
		return structuredClone(currentActiveContexts);
	}
	return {
		ingest,
		reset,
		snapshot,
		activeContexts: () => structuredClone(currentActiveContexts),
		contextHistory: () => [...currentContextHistory]
	};
}
//#endregion
//#region src/runtime/llm-service.ts
/**
* Normalize chat messages so they match the wire format the active provider
* actually accepts, flattening content-part arrays back to plain strings when
* the provider can't deserialize arrays.
*
* Use when:
* - Composing the final message list right before handing it to the OpenAI-
*   compatible chat SDK.
*
* Expects:
* - `role: 'error'` entries (AIRI-internal markers from the chat UI). They are
*   rewritten as user-role narrations so the provider doesn't reject them.
* - `content` may be a string, a content-part array, or undefined.
*
* Returns:
* - A new array of `Message` values; original objects are not mutated.
*
* @param messages - Raw messages from the chat session, may include AIRI's
*   `error` role.
* @param supportsContentArray - When `false`, force-flatten every array
*   content (including text + `image_url` mixes) to a text-only string and
*   drop non-text parts. Drives the runtime auto-degrade for strict providers.
*   Defaults to `true` to preserve vision/multimodal payloads on capable
*   providers.
*/
function sanitizeMessages(messages, supportsContentArray = true) {
	return messages.map((message) => {
		if (message && message.role === "error") return {
			role: "user",
			content: `User encountered error: ${String(message.content ?? "")}`
		};
		if (message && Array.isArray(message.content)) {
			const contentParts = message.content;
			const hasNonTextPart = contentParts.some((part) => part?.type && part.type !== "text");
			if (!supportsContentArray || !hasNonTextPart) return {
				...message,
				content: contentParts.map((part) => part?.text ?? "").join("")
			};
		}
		return message;
	});
}
function modelKey(model, chatProvider) {
	return `${chatProvider.chat(model).baseURL}-${model}`;
}
function streamOptionsToolsCompatibilityOk(model, chatProvider, options) {
	if (options?.supportsTools !== void 0) return options.supportsTools;
	const key = modelKey(model, chatProvider);
	return options?.toolsCompatibility?.get(key) !== false;
}
/**
* Resolve whether the active model+provider currently supports content-part
* arrays. Defaults to `true` so first-time calls keep multimodal payloads;
* flips to `false` once {@link isContentArrayRelatedError} has fired on this
* model key and the caller has cached the degrade in
* {@link StreamOptions.contentArrayCompatibility}.
*/
function streamOptionsContentArrayCompatibilityOk(model, chatProvider, options) {
	if (options?.supportsContentArray !== void 0) return options.supportsContentArray;
	const key = modelKey(model, chatProvider);
	return options?.contentArrayCompatibility?.get(key) !== false;
}
async function resolveTools(options) {
	return (typeof options?.tools === "function" ? await options.tools() : options?.tools) ?? [];
}
function isAbortError(error) {
	return typeof error === "object" && error !== null && error.name === "AbortError";
}
function createCapturedToolErrorResult(toolName, error) {
	return `Tool call error for "${toolName}": ${errorMessageFrom(error) ?? String(error)}`;
}
function withCapturedToolErrors(tools, capturedToolErrorByCallId) {
	return tools.map((tool) => ({
		...tool,
		execute: async (input, executeOptions) => {
			try {
				return await tool.execute(input, executeOptions);
			} catch (error) {
				if (isAbortError(error)) throw error;
				const result = createCapturedToolErrorResult(tool.function.name, error);
				capturedToolErrorByCallId.set(executeOptions.toolCallId, result);
				return result;
			}
		}
	}));
}
function resolveCapturedToolErrorEvent(event, capturedToolErrorByCallId) {
	if (typeof event !== "object" || event === null || event.type !== "tool-result" || typeof event.toolCallId !== "string") return event;
	const toolCallId = event.toolCallId;
	const result = capturedToolErrorByCallId.get(toolCallId);
	if (result == null) return event;
	capturedToolErrorByCallId.delete(toolCallId);
	return {
		...event,
		type: "tool-error",
		isError: true,
		result
	};
}
async function streamFrom({ model, chatProvider, messages, options, builtinToolsResolver }) {
	const chatConfig = chatProvider.chat(model);
	const sanitized = sanitizeMessages(messages, streamOptionsContentArrayCompatibilityOk(model, chatProvider, options));
	const supportedTools = streamOptionsToolsCompatibilityOk(model, chatProvider, options);
	const builtinTools = supportedTools ? await (builtinToolsResolver?.(model, chatProvider) ?? Promise.resolve([])) : [];
	const customTools = supportedTools ? await resolveTools(options) : [];
	const mergedTools = supportedTools ? [...builtinTools, ...customTools] : [];
	const tools = mergedTools.length > 0 ? mergedTools : void 0;
	const capturedToolErrorByCallId = /* @__PURE__ */ new Map();
	const streamTools = options?.captureToolErrors && tools != null ? withCapturedToolErrors(tools, capturedToolErrorByCallId) : tools;
	return new Promise((resolve, reject) => {
		let settled = false;
		const resolveOnce = () => {
			if (settled) return;
			settled = true;
			resolve();
		};
		const rejectOnce = (error) => {
			if (settled) return;
			settled = true;
			reject(error);
		};
		const onEvent = async (event) => {
			try {
				const streamEvent = resolveCapturedToolErrorEvent(event, capturedToolErrorByCallId);
				await options?.onStreamEvent?.(streamEvent);
				if (event && event.type === "finish") {
					const finishReason = event.finishReason;
					if (!(finishReason === "tool_calls" || finishReason === "tool-calls") || !options?.waitForTools) resolveOnce();
				} else if (event && event.type === "error") rejectOnce(event.error ?? /* @__PURE__ */ new Error("Stream error"));
			} catch (error) {
				rejectOnce(error);
			}
		};
		try {
			const streamResult = streamText({
				...chatConfig,
				abortSignal: options?.abortSignal,
				messages: sanitized,
				headers: options?.headers,
				stopWhen: stepCountAtLeast(10),
				tools: streamTools,
				onEvent
			});
			streamResult.steps.then(resolveOnce).catch((error) => {
				rejectOnce(error);
				console.error("Stream steps error:", error);
			});
			streamResult.messages.catch((error) => console.error("Stream messages error:", error));
			streamResult.usage.catch((error) => console.error("Stream usage error:", error));
			streamResult.totalUsage.catch((error) => console.error("Stream totalUsage error:", error));
		} catch (error) {
			rejectOnce(error);
		}
	});
}
const TOOLS_RELATED_ERROR_PATTERNS = [
	/does not support tools/i,
	/no endpoints found that support tool use/i,
	/invalid schema for function/i,
	/invalid.?function.?parameters/i,
	/functions are not supported/i,
	/unrecognized request argument.+tools/i,
	/tool use with function calling is unsupported/i,
	/tool_use_failed/i,
	/does not support function.?calling/i,
	/tools?\s+(is|are)\s+not\s+supported/i
];
function isToolRelatedError(error) {
	const message = String(error);
	return TOOLS_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
const CONTENT_ARRAY_RELATED_ERROR_PATTERNS = [/messages\[\d+\][^"]*invalid type:\s*sequence,\s*expected\s+a\s+string/i, /messages\.\d+\.content[^"]*(?:expected|should be).*string/i];
/**
* Whether the given error indicates the provider rejected content-part arrays
* and the caller should auto-degrade to string-only `content` for this model.
*
* Use when:
* - Catching errors thrown by {@link streamFrom} so the chat store can flip
*   `contentArrayCompatibility` for the failing model key.
*
* Expects:
* - `error` may be an Error instance, a thrown SDK response object, a string,
*   or anything else; we coerce via `String(error)` and pattern-match.
*
* Returns:
* - `true` when the message matches a known "content array unsupported" wire
*   format from an OpenAI-compatible gateway, otherwise `false`.
*/
function isContentArrayRelatedError(error) {
	const message = String(error);
	return CONTENT_ARRAY_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
//#endregion
//#region src/session/merge-loaded-session-messages.ts
function extractMessageContent(message) {
	if (typeof message.content === "string") return message.content;
	if (Array.isArray(message.content)) return message.content.map((part) => {
		if (typeof part === "string") return part;
		if (part && typeof part === "object" && "text" in part) return String(part.text ?? "");
		return "";
	}).join("");
	return "";
}
function getMessageFingerprint(message) {
	return [
		message.id ?? "",
		message.role,
		message.createdAt ?? "",
		extractMessageContent(message)
	].join("");
}
function mergeLoadedSessionMessages(storedMessages, currentMessages) {
	if (currentMessages.length === 0) return storedMessages;
	const currentNonSystemMessages = currentMessages.filter((message, index) => index !== 0 || message.role !== "system");
	if (currentNonSystemMessages.length === 0) return storedMessages;
	const seen = new Set(storedMessages.map(getMessageFingerprint));
	const extraMessages = currentNonSystemMessages.filter((message) => {
		const fingerprint = getMessageFingerprint(message);
		if (seen.has(fingerprint)) return false;
		seen.add(fingerprint);
		return true;
	});
	if (extraMessages.length === 0) return storedMessages;
	const systemMessage = storedMessages[0]?.role === "system" ? storedMessages[0] : currentMessages[0]?.role === "system" ? currentMessages[0] : void 0;
	if (storedMessages.length === 0 && systemMessage) return [systemMessage, ...extraMessages];
	return [...storedMessages, ...extraMessages];
}
//#endregion
export { createChatHooks, createContextRegistry, isContentArrayRelatedError, isToolRelatedError, mergeLoadedSessionMessages, modelKey, sanitizeMessages, streamFrom, streamOptionsContentArrayCompatibilityOk, streamOptionsToolsCompatibilityOk };
